import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { backpackAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { type Settlement, settlementsResponseSchema } from './schema'

/** 1 ページあたり API が返す最大件数 (Backpack 仕様の上限) */
const PAGE_LIMIT = 1000

/**
 * 安全装置として 1 回の `fetchApiData` 呼び出しで取得する最大ページ数。
 * 1000 件 × 1000 ページ = 100 万件まで耐える設計とする。
 */
const MAX_PAGES = 1000

/** Backpack の先物精算通貨。settlement には資産の項目がなく、常に USDC で精算される */
const SETTLEMENT_SYMBOL = 'USDC'

/**
 * naive ISO date-time を UTC として解釈し UNIX milliseconds に変換する。
 */
const parseTimestamp = (value: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(value)
  const isoString = hasTimezone ? value : `${value}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack settlement timestamp: ${value}`)
  }
  return ms
}

/**
 * Backpack 先物の実現損益履歴を取得し、CSV (`backpack-ledger` の `realized_pnl`) と同じ方針で
 * `realize-profit` (利益) / `realize-loss` (損失) Action を生成する。
 *
 * `/wapi/v1/history/fills` は先物約定の実現損益を含まず、`/history/pnl` は廃止済みのため、
 * settlement 履歴を `source=RealizePnl` で絞り込んで利用する。
 * 実現損益は手数料・ファンディングを含まない価格差損益 (それぞれ fill-history / funding-history で計上)。
 *
 * 当 endpoint は `from`/`to` フィルタを持たず offset ベースの pagination のみ。
 * sortDirection=Desc (新しい順) で取得し、 framework から渡された `from` より古い record に達したら
 * early-stop することで効率化する。
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const collected: (Settlement & { timestampMs: number })[] = []
  let earlyStop = false

  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await client.run({
      method: 'GET',
      path: '/wapi/v1/history/settlement',
      query: {
        limit: PAGE_LIMIT.toString(),
        offset: (page * PAGE_LIMIT).toString(),
        sortDirection: 'Desc',
        source: 'RealizePnl',
      },
      responseSchema: settlementsResponseSchema,
    })

    for (const item of response) {
      const timestampMs = parseTimestamp(item.timestamp)
      if (from !== undefined && timestampMs < from) {
        earlyStop = true
        break
      }
      if (to !== undefined && timestampMs > to) {
        continue
      }
      collected.push({ ...item, timestampMs })
    }

    if (earlyStop || response.length < PAGE_LIMIT) {
      break
    }
  }

  collected.sort((a, b) => a.timestampMs - b.timestampMs)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of collected) {
    fetchedFrom =
      fetchedFrom === undefined
        ? item.timestampMs
        : Math.min(fetchedFrom, item.timestampMs)
    fetchedTo =
      fetchedTo === undefined
        ? item.timestampMs
        : Math.max(fetchedTo, item.timestampMs)

    // filter 指定に関わらず、RealizePnl 以外が返ってきた場合は計上しない
    if (item.source !== 'RealizePnl') {
      continue
    }
    if (item.quantity.value === 0n) {
      continue
    }

    const asset = getAsset(SETTLEMENT_SYMBOL)
    if (!asset) {
      throw new AssetNotFoundException(SETTLEMENT_SYMBOL)
    }

    const isProfit = item.quantity.value > 0n
    const positionId = item.positionId ?? 'unknown'
    const sequence =
      item.engineSequence === undefined || item.engineSequence === null
        ? ''
        : `_${item.engineSequence}`

    actions.push({
      comment: `Realized PnL of position ${positionId}`,
      evidence: 'system-rule',
      order: 0,
      source: `${backpackAccountCategory}_${config.type}_${positionId}_${item.timestampMs}${sequence}`,
      type: isProfit ? 'realize-profit' : 'realize-loss',
      timestamp: item.timestampMs,
      transfers: [
        {
          asset,
          amount: absoluteValue(item.quantity),
          direction: isProfit ? 'in' : 'out',
        },
      ],
    })
  }

  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: collected.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
